// The frame of Kitchen and Counter (docs/plans/stations.md): no sidebar, one bar across the top,
// and the banners and sync state a device still needs.
import { Button, FilterPill, FilterPills, useToastCorner, type Area } from '@ims/ui'
import { Bell, BellRing, ChefHat, Coffee, LogOut, Maximize } from '@ims/ui/icons'
import { useEffect, useState, type ReactNode } from 'react'
import { Outlet, useSearchParams } from 'react-router'
import { useNow, useOnline } from '../hooks.ts'
import { setSound, useAlertSound } from '../lib/alerts.ts'
import { clock } from '../lib/orders.ts'
import type { StationMode } from '../lib/station.ts'
import { useKitchenTickets } from '../screens/floor.ts'
import { useSession } from '../session.tsx'
import { AlertsBell } from './alerts.tsx'
import { Banners, SyncIndicator } from './shell.tsx'
import { StationLeaveDialog } from './station-dialogs.tsx'

const BAR_BUTTON =
  'flex size-11 shrink-0 items-center justify-center rounded-xl border border-border text-muted hover:bg-soft hover:text-ink'

function KitchenPills() {
  const tickets = useKitchenTickets() ?? []
  const [params, setParams] = useSearchParams()
  const now = params.get('station')
  const pill = (id: 'kitchen' | 'bar' | null, label: string, area?: Area, icon?: ReactNode) => (
    <FilterPill
      key={label}
      selected={now === id}
      onSelect={() => setParams(id ? { station: id } : {}, { replace: true })}
      area={area}
      icon={icon}
      count={tickets.filter((t) => !id || t.ticket.station === id).length}
    >
      {label}
    </FilterPill>
  )
  return (
    <FilterPills label="Station">
      {pill(null, 'All')}
      {pill('kitchen', 'Kitchen', 'kitchen', <ChefHat className="size-4" aria-hidden />)}
      {pill('bar', 'Bar', 'take', <Coffee className="size-4" aria-hidden />)}
    </FilterPills>
  )
}

function Clock() {
  const now = useNow()
  return (
    <span className="tabular hidden whitespace-nowrap text-xl font-extrabold sm:inline">
      {clock(now.toISOString())}
    </span>
  )
}

export function StationShell({
  mode,
  left,
}: {
  mode: Exclude<StationMode, 'full'>
  /** What the bar holds on the left in this mode. */
  left?: ReactNode
}) {
  const { platform } = useSession()
  const sound = useAlertSound()
  const online = useOnline()
  const [leaving, setLeaving] = useState(false)
  useToastCorner()
  const [fullscreen, setFullscreen] = useState(() => platform.station?.isFullscreen() ?? true)
  useEffect(() => platform.station?.keepAwake(), [platform])
  useEffect(() => {
    const sync = () => setFullscreen(platform.station?.isFullscreen() ?? true)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [platform])
  return (
    <div className="flex h-dvh flex-col bg-background">
      <header className="flex min-h-16 shrink-0 flex-wrap items-center gap-1.5 border-b border-border bg-surface px-3 py-2 sm:px-4">
        {mode === 'kitchen' ? <KitchenPills /> : left}
        <div className="ml-auto flex items-center gap-1.5">
          {mode === 'kitchen' && <Clock />}
          <SyncIndicator />
          {!fullscreen && platform.station && (
            <button
              type="button"
              aria-label="Full screen"
              title="Full screen"
              className={BAR_BUTTON}
              onClick={() => platform.station?.enterFullscreen()}
            >
              <Maximize className="size-[18px]" aria-hidden />
            </button>
          )}
          {mode === 'kitchen' && (
            <button
              type="button"
              aria-label={sound ? 'Turn the chime off' : 'Turn the chime on'}
              aria-pressed={sound}
              className={BAR_BUTTON}
              onClick={() => setSound(!sound)}
            >
              {sound ? (
                <BellRing className="size-[18px]" aria-hidden />
              ) : (
                <Bell className="size-[18px]" aria-hidden />
              )}
            </button>
          )}
          {mode === 'counter' && <AlertsBell />}
          <Button
            variant="secondary"
            aria-label={`Leave ${mode === 'kitchen' ? 'Kitchen' : 'Counter'}`}
            className="h-11"
            icon={<LogOut className="size-[18px]" />}
            onClick={() => setLeaving(true)}
          >
            Leave
          </Button>
        </div>
      </header>
      {/* Offline, this mode's screen says what it means for the kitchen. */}
      {online && <Banners />}
      <main id="main" tabIndex={-1} className="relative flex-1 overflow-y-auto focus:outline-none">
        <Outlet />
      </main>
      {leaving && <StationLeaveDialog mode={mode} onClose={() => setLeaving(false)} />}
    </div>
  )
}
